const TEXT_ATTRIBUTES = ['placeholder', 'title', 'aria-label']
const ORIGINAL_TEXT_BY_NODE = new WeakMap()
const LAST_TRANSLATED_TEXT_BY_NODE = new WeakMap()
const ORIGINAL_ATTRS_BY_ELEMENT = new WeakMap()
const LAST_TRANSLATED_ATTRS_BY_ELEMENT = new WeakMap()

const translateLiteral = (raw, map) => {
  const value = String(raw || '')
  if (!value) return value
  if (Object.prototype.hasOwnProperty.call(map, value)) {
    return String(map[value])
  }
  const trimmed = value.trim()
  if (!trimmed || !Object.prototype.hasOwnProperty.call(map, trimmed)) {
    return value
  }
  const prefixLen = value.indexOf(trimmed)
  const suffixLen = value.length - prefixLen - trimmed.length
  return `${value.slice(0, prefixLen)}${String(map[trimmed])}${value.slice(value.length - suffixLen)}`
}

const shouldSkipNode = (node) => {
  if (!node) return true
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node
    if (el.closest && el.closest('[data-i18n-skip="1"]')) return true
    const tag = String(el.tagName || '').toLowerCase()
    return tag === 'script' || tag === 'style' || tag === 'noscript'
  }
  if (node.nodeType === Node.TEXT_NODE) {
    return shouldSkipNode(node.parentElement)
  }
  return false
}

const isLiveNode = (node) => {
  if (!node) return false
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node
    return Boolean(el.closest && el.closest('[data-i18n-live="1"]'))
  }
  if (node.nodeType === Node.TEXT_NODE) {
    return isLiveNode(node.parentElement)
  }
  return false
}

const translateTextNode = (textNode, map) => {
  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return
  if (shouldSkipNode(textNode)) return
  const liveValue = textNode.nodeValue || ''
  if (isLiveNode(textNode)) {
    ORIGINAL_TEXT_BY_NODE.set(textNode, liveValue)
    const translatedLive = translateLiteral(liveValue, map)
    if (translatedLive !== liveValue) {
      textNode.nodeValue = translatedLive
    }
    LAST_TRANSLATED_TEXT_BY_NODE.set(textNode, translatedLive)
    return
  }
  const hadOriginal = ORIGINAL_TEXT_BY_NODE.has(textNode)
  let original = hadOriginal ? (ORIGINAL_TEXT_BY_NODE.get(textNode) || '') : liveValue
  if (!hadOriginal) {
    ORIGINAL_TEXT_BY_NODE.set(textNode, original)
  }
  const lastTranslated = LAST_TRANSLATED_TEXT_BY_NODE.get(textNode)
  // React/content updates may replace node text. If this isn't our own last translation,
  // treat the current value as the new source literal.
  if (hadOriginal && liveValue !== original && liveValue !== lastTranslated) {
    original = liveValue
    ORIGINAL_TEXT_BY_NODE.set(textNode, original)
  }
  const translated = translateLiteral(original, map)
  if (translated !== liveValue) {
    textNode.nodeValue = translated
  }
  LAST_TRANSLATED_TEXT_BY_NODE.set(textNode, translated)
}

const translateElementAttributes = (element, map) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return
  if (shouldSkipNode(element)) return
  const liveElement = isLiveNode(element)
  let originalAttrs = ORIGINAL_ATTRS_BY_ELEMENT.get(element)
  if (!originalAttrs) {
    originalAttrs = {}
    ORIGINAL_ATTRS_BY_ELEMENT.set(element, originalAttrs)
  }
  let lastTranslatedAttrs = LAST_TRANSLATED_ATTRS_BY_ELEMENT.get(element)
  if (!lastTranslatedAttrs) {
    lastTranslatedAttrs = {}
    LAST_TRANSLATED_ATTRS_BY_ELEMENT.set(element, lastTranslatedAttrs)
  }
  for (const attr of TEXT_ATTRIBUTES) {
    if (!element.hasAttribute(attr)) continue
    const liveValue = element.getAttribute(attr) || ''
    const hasOriginal = Object.prototype.hasOwnProperty.call(originalAttrs, attr)
    let original = hasOriginal ? String(originalAttrs[attr] || '') : liveValue
    if (!hasOriginal) {
      originalAttrs[attr] = original
    }
    if (liveElement) {
      original = liveValue
      originalAttrs[attr] = original
    }
    const lastTranslated = Object.prototype.hasOwnProperty.call(lastTranslatedAttrs, attr)
      ? String(lastTranslatedAttrs[attr] || '')
      : undefined
    if (hasOriginal && liveValue !== original && liveValue !== lastTranslated) {
      original = liveValue
      originalAttrs[attr] = original
    }
    const translated = translateLiteral(original, map)
    if (translated !== liveValue) {
      element.setAttribute(attr, translated)
    }
    lastTranslatedAttrs[attr] = translated
  }
}

const translateSubtree = (root, map) => {
  if (!root) return
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root, map)
    return
  }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return

  if (root.nodeType === Node.ELEMENT_NODE) {
    translateElementAttributes(root, map)
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT)
  let current = walker.currentNode
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      translateTextNode(current, map)
    } else if (current.nodeType === Node.ELEMENT_NODE) {
      translateElementAttributes(current, map)
    }
    current = walker.nextNode()
  }
}

export const installDomTranslator = (root, map) => {
  if (!root || !map || Object.keys(map).length === 0) {
    return () => {}
  }
  translateSubtree(root, map)
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData') {
        translateTextNode(record.target, map)
        continue
      }
      if (record.type === 'attributes') {
        translateElementAttributes(record.target, map)
        continue
      }
      if (record.type === 'childList') {
        for (const added of record.addedNodes || []) {
          translateSubtree(added, map)
        }
      }
    }
  })
  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: TEXT_ATTRIBUTES
  })
  return () => observer.disconnect()
}
